/**
 * ListRow — THE row, extracted.
 *
 * There were five hand-written implementations of the same anatomy on this
 * branch — the review documents list, the matter sources list, tasks, notes,
 * and the Q&A list — each re-deriving the same grid, the same hover
 * behaviour, the same reveal delay, and each drifting a little. This is that
 * anatomy once:
 *
 *     [ glyph 26px │ title + one meta line │ right slot ] + hover actions
 *
 * The rules the copies kept re-learning, now enforced here:
 *
 *   • ALL THREE COLUMNS ALWAYS EXIST. A row without a right slot leaves the
 *     same gap a row with one occupies, so the list can be scanned down.
 *   • CONDITIONAL CONTENT LIVES INSIDE A FIXED SLOT. The glyph changes state;
 *     it does not appear and disappear. Nothing may change the row's height.
 *   • ONE META LINE. Not two, not a wrapping paragraph — one line that
 *     truncates.
 *   • ACTIONS ARE REVEALED, NOT STACKED. They sit over the right slot's
 *     trailing space on hover, which is why that slot shifts left instead of
 *     fading out: hiding the status exactly as someone reaches for the row is
 *     worse than moving it.
 */

import React from 'react';
import clsx from 'clsx';

export interface ListRowProps {
  /** Fixed 26px slot. Swap its CONTENT for state; never omit it. */
  glyph: React.ReactNode;
  title: React.ReactNode;
  /** One line. Truncates — pass a string or inline nodes, never a block. */
  meta?: React.ReactNode;
  /** Fixed-width trailing slot: badges, avatars, counts. */
  right?: React.ReactNode;
  /** Width of the right slot. Keep it constant within one list. */
  rightWidth?: string;
  /** Revealed on hover; absolutely positioned, so it never affects layout. */
  actions?: React.ReactNode;
  selected?: boolean;
  /** Dimmed and non-interactive — mid-delete, or archived. */
  muted?: boolean;
  onClick?: () => void;
  /** Staggered reveal, as the research and review lists do. */
  index?: number;
  title_?: string;
  'data-testid'?: string;
  [key: `data-${string}`]: any;
}

const ListRow: React.FC<ListRowProps> = ({
  glyph,
  title,
  meta,
  right,
  rightWidth = 'w-[136px]',
  actions,
  selected = false,
  muted = false,
  onClick,
  index = 0,
  title_,
  ...rest
}) => {
  const interactive = !!onClick;

  return (
    <div
      data-testid={rest['data-testid'] ?? 'list-row'}
      data-selected={selected}
      title={title_}
      style={{ '--reveal-delay': `${Math.min(index, 8) * 30}ms` } as React.CSSProperties}
      className={clsx(
        'group relative reveal-item transition-colors duration-200 ease-slide',
        muted && 'opacity-60 pointer-events-none',
        selected ? 'bg-champagne/10' : 'hover:bg-surface/60'
      )}
      {...rest}
    >
      {/* Flush to the edge, solid, thick enough to read as a marker — a
          hairline rail was the main reason a selected row did not look it. */}
      <span
        aria-hidden
        className={clsx(
          'absolute left-0 top-0 bottom-0 w-[3px] bg-champagne',
          'transition-opacity duration-200 ease-slide',
          selected ? 'opacity-100' : 'opacity-0'
        )}
      />

      {React.createElement(
        interactive ? 'button' : 'div',
        {
          ...(interactive ? { type: 'button', onClick } : {}),
          className: 'w-full text-left px-3 py-2.5',
        },
        <div className="grid grid-cols-[26px_1fr_auto] items-center gap-3">
          <span className="w-[26px] h-[26px] flex items-center justify-center flex-shrink-0">
            {glyph}
          </span>

          <div className="min-w-0">
            <p
              className={clsx(
                'text-[13px] truncate leading-snug transition-colors duration-200 ease-slide',
                selected ? 'font-semibold text-fg-strong' : 'font-medium text-fg-body'
              )}
            >
              {title}
            </p>
            {meta !== undefined && (
              <p className="text-[11px] text-fg-muted truncate mt-0.5 flex items-center gap-1.5">
                {meta}
              </p>
            )}
          </div>

          <div
            className={clsx(
              'flex items-center justify-end gap-1.5 flex-shrink-0',
              rightWidth,
              actions && 'transition-[margin] duration-200 ease-slide group-hover:mr-14'
            )}
          >
            {right}
          </div>
        </div>
      )}

      {actions && (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5
                     opacity-0 group-hover:opacity-100 focus-within:opacity-100
                     transition-opacity duration-200 ease-slide"
        >
          {actions}
        </div>
      )}
    </div>
  );
};

/** Separator between meta-line facts. */
export const MetaDot: React.FC<{ className?: string }> = ({ className }) => (
  <span aria-hidden className={clsx('text-fg-muted/60', className)}>·</span>
);

/** The 26px glyph disc, in its three standard states. */
export const RowGlyph: React.FC<{
  state?: 'default' | 'done' | 'pending' | 'accent';
  children: React.ReactNode;
}> = ({ state = 'default', children }) => (
  <span
    data-testid="row-glyph"
    data-state={state}
    className={clsx(
      'w-[26px] h-[26px] rounded-full flex items-center justify-center flex-shrink-0',
      state === 'done' && 'bg-success-500 text-white',
      state === 'pending' && 'border border-dashed border-hairline text-fg-muted',
      state === 'accent' && 'border border-champagne/50 bg-champagne/10 text-champagne',
      state === 'default' && 'border border-hairline-soft bg-surface-sunken/70 text-fg-muted'
    )}
  >
    {children}
  </span>
);

export default ListRow;
